import React, { useEffect, useRef } from "react";
import { useAuthUser } from "../hooks/AuthProvider";
import { useAuthStatus } from "../hooks/useAuthStatus";
import { syncCurrentUserSession } from "../helpers/firebaseAuth";
import FullScreenSpinner from "./Common/FullScreenSpinner";

const REFRESH_INTERVAL_MS = 55 * 60 * 1000; // 55 minutes — before the 60-min token expiry
const STALE_AFTER_MS = 10 * 60 * 1000; // resync immediately if it's been longer than this

/**
 * Silently refreshes the HttpOnly session cookie by getting a new Firebase ID
 * token and posting it to the server. A plain setInterval isn't reliable on
 * its own — browsers throttle/pause timers in backgrounded tabs and pause
 * them entirely while the device sleeps, so a user who steps away for a
 * while can come back to a stale cookie even though the interval "should"
 * have fired. To cover that, we also resync whenever the tab regains
 * visibility/focus if it's been longer than STALE_AFTER_MS since the last
 * refresh, catching up immediately instead of waiting for the next tick.
 */
function useTokenRefresh(user) {
  const lastRefreshRef = useRef(0);

  useEffect(() => {
    if (!user) return;

    const refresh = async () => {
      try {
        const didSync = await syncCurrentUserSession();
        if (didSync) lastRefreshRef.current = Date.now();
      } catch (e) {
        console.warn("Token refresh failed:", e);
      }
    };

    const refreshIfStale = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastRefreshRef.current >= STALE_AFTER_MS) refresh();
    };

    refresh(); // sync once immediately on mount/sign-in
    const intervalId = setInterval(refresh, REFRESH_INTERVAL_MS);
    document.addEventListener("visibilitychange", refreshIfStale);
    window.addEventListener("focus", refreshIfStale);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", refreshIfStale);
      window.removeEventListener("focus", refreshIfStale);
    };
  }, [user]);
}

export const AppInitializer = ({ children }) => {
    const user = useAuthUser(); // shared AuthProvider listener — avoids a second onAuthStateChanged subscription
    const { isLoading } = useAuthStatus();
    useTokenRefresh(user);
  
    if (isLoading) {
      return <FullScreenSpinner />;
    }
  
    return <>{children}</>;
  };
